import React from 'react';
import classNames from 'classnames';
import { useIntl, defineMessages } from 'react-intl';
import { TooltipUI, ArrowDirection } from 'hellospa/foundation/control/tooltip';
import { TemplateResponse } from 'hellospa/app-context/actions/prep-and-send';
import { Template, RecipientTypes } from 'hellospa/page/prep-and-send/data/types';
import styles from './styles.scss';

type Props = {
  index: number,
  template: Template,
  onSelectTemplate: (template: Template) => () => void,
  templatesInRequest: TemplateResponse[],
  isBulkSend: boolean,
}

const messages = defineMessages({
  incompatibleSignerOrder: {
    id: '',
    description: 'tooltip message for templates that are incompatible because of signer order',
    defaultMessage: 'Signer order setting is incompatible with currently selected template(s)',
  },
  incompatibleDateFormat: {
    id: '',
    description: 'tooltip message for templates that are incompatible because of date format',
    defaultMessage: 'Date format setting is incompatible with currently selected template(s)',
  },
  incompatibleSignerReassignment: {
    id: '',
    description: 'tooltip message for templates that are incompatible because of signer reassignment',
    defaultMessage: 'Signer reassignment setting is incompatible with currently selected template(s)',
  },
  incompatibleSignerRoles: {
    id: '',
    description: 'tooltip message for templates that are incompatible because of role names',
    defaultMessage: 'Signer role is incompatible with currently selected template(s)',
  },
});

const NOOP = () => { };

const rolesAreCompatible = (first: TemplateResponse, second: Template) => {
  if (first.recipients.length === 1 && second.roles.length === 1) {
    const firstRole = first.recipients[0];
    return firstRole.type === RecipientTypes.Role && firstRole.name === second.roles[0];
  }
  return false;
};

export default function Item({
  index, template, onSelectTemplate, templatesInRequest, isBulkSend,
}: Props) {
  const intl = useIntl();
  // we can check with the first template in the request because all of the
  // templates in the request should be similar
  const templateInRequest = templatesInRequest[0] || null;
  let isDisabled = false;
  let message;

  if (templateInRequest) {
    if (template.dateFormat !== templateInRequest.dateFormat) {
      // if the date formats don't match, the template is disabled
      message = intl.formatMessage(messages.incompatibleDateFormat);
      isDisabled = true;
    } else if (
      // cannot combine ordered templates with any other templates
      templateInRequest.recipientOrder
      || (template.isOrdered && !templateInRequest.recipientOrder)
    ) {
      message = intl.formatMessage(messages.incompatibleSignerOrder);
      isDisabled = true;
    } else if (
      (templateInRequest.recipientReassignment && !template.allowReassign)
      || (!templateInRequest.recipientReassignment && template.allowReassign)
    ) {
      // mismatched signer reassignment setting is also not allowed
      // logical xor would be a straight forward condition
      message = intl.formatMessage(messages.incompatibleSignerReassignment);
      isDisabled = true;
    } else if (isBulkSend && !rolesAreCompatible(templateInRequest, template)) {
      message = intl.formatMessage(messages.incompatibleSignerRoles);
      isDisabled = true;
    }
  }

  // since the item has relative positioning and the tooltip uses that to position itself,
  // the first item's tooltip is displayed at the bottom so it's visible to the user
  const tooltip = isDisabled ? (
    <TooltipUI
      className={classNames(styles.tooltip, {
        [styles.firstToolTip]: index === 0,
      })}
      arrow={index === 0 ? ArrowDirection.Top : ArrowDirection.Bottom}
    >
      {message}
    </TooltipUI>
  ) : null;

  return (
    <li
      className={classNames(styles.templateItem, {
        [styles.isDisabled]: isDisabled,
      })}
      data-qa-ref="template-item"
      key={template.id}
      onClick={isDisabled ? NOOP : onSelectTemplate(template)}
    >
      {tooltip}
      <span>{template.title}</span>
    </li>
  );
}
